import { useState, useEffect, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Search, Filter, ArrowUpDown, Flame, Footprints } from 'lucide-react'
import { customerApi, resolveImageUrl } from '../services/api'
import { useRestaurant } from '../contexts/RestaurantContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useCart } from '../contexts/CartContext'
import { HomeShimmer } from '../components/Shimmer'
import AllergenIcons from '../components/AllergenIcons'
import { getLocalizedName, getLocalizedDescription } from '../utils/localization'

interface MenuItem {
  id: string
  name: string
  description: string
  base_price: number
  code?: string
  image?: string
  is_popular: boolean
  category_id: string
  calories?: number
  steps_to_burn?: number
  allergens?: string[]
  categories?: {
    id: string
    name: string
    color?: string
  }
}

interface Category {
  id: string
  name: string
  icon?: string
  color?: string
}

export default function Home() {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [sortPanelOpen, setSortPanelOpen] = useState(false)
  const [sortClosing, setSortClosing] = useState(false)
  const [filterPanelOpen, setFilterPanelOpen] = useState(false)
  const [filterClosing, setFilterClosing] = useState(false)
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([])
  const [sortBy, setSortBy] = useState<'default' | 'price_asc' | 'price_desc' | 'name' | 'category_az'>('default')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const SHEET_CLOSE_MS = 250
  const DRAG_CLOSE_THRESHOLD = 80
  const [sortDragOffset, setSortDragOffset] = useState(0)
  const [sortSnapBack, setSortSnapBack] = useState(false)
  const [filterDragOffset, setFilterDragOffset] = useState(0)
  const [filterSnapBack, setFilterSnapBack] = useState(false)
  const dragStartY = useRef(0)
  const dragStartOffset = useRef(0)
  const sortDragOffsetRef = useRef(0)
  const filterDragOffsetRef = useRef(0)
  const sortCanDrag = useRef(false)
  const filterCanDrag = useRef(false)
  const { restaurantInfo, formatPrice, allergenMap } = useRestaurant()
  const { t, isRTL, language, setLanguage } = useLanguage()
  const { addItem } = useCart()

  useEffect(() => {
    if (!sortPanelOpen || sortClosing) return
    const t = setTimeout(() => { sortCanDrag.current = true }, 350)
    return () => clearTimeout(t)
  }, [sortPanelOpen, sortClosing])

  useEffect(() => {
    if (!filterPanelOpen || filterClosing) return
    const t = setTimeout(() => { filterCanDrag.current = true }, 350)
    return () => clearTimeout(t)
  }, [filterPanelOpen, filterClosing])

  const handleSortDragStart = (clientY: number) => {
    if (!sortCanDrag.current) return
    dragStartY.current = clientY
    dragStartOffset.current = sortDragOffset
  }
  const handleSortDragMove = (clientY: number) => {
    const delta = clientY - dragStartY.current
    const newOffset = Math.max(0, dragStartOffset.current + delta)
    const clamped = Math.min(newOffset, 400)
    sortDragOffsetRef.current = clamped
    setSortDragOffset(clamped)
  }
  const handleSortDragEnd = () => {
    const offset = sortDragOffsetRef.current
    setSortDragOffset(0)
    if (offset >= DRAG_CLOSE_THRESHOLD) {
      closeSortPanel()
    } else {
      setSortSnapBack(true)
      setTimeout(() => setSortSnapBack(false), 200)
    }
  }

  const handleFilterDragStart = (clientY: number) => {
    if (!filterCanDrag.current) return
    dragStartY.current = clientY
    dragStartOffset.current = filterDragOffset
  }
  const handleFilterDragMove = (clientY: number) => {
    const delta = clientY - dragStartY.current
    const newOffset = Math.max(0, dragStartOffset.current + delta)
    const clamped = Math.min(newOffset, 400)
    filterDragOffsetRef.current = clamped
    setFilterDragOffset(clamped)
  }
  const handleFilterDragEnd = () => {
    const offset = filterDragOffsetRef.current
    setFilterDragOffset(0)
    if (offset >= DRAG_CLOSE_THRESHOLD) {
      closeFilterPanel()
    } else {
      setFilterSnapBack(true)
      setTimeout(() => setFilterSnapBack(false), 200)
    }
  }

  const bindSortMouseDrag = () => {
    const onMove = (e: MouseEvent) => handleSortDragMove(e.clientY)
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      handleSortDragEnd()
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }
  const bindFilterMouseDrag = () => {
    const onMove = (e: MouseEvent) => handleFilterDragMove(e.clientY)
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      handleFilterDragEnd()
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const closeSortPanel = () => {
    if (sortClosing) return
    sortCanDrag.current = false
    setSortClosing(true)
    setSortDragOffset(0)
    setTimeout(() => {
      setSortPanelOpen(false)
      setSortClosing(false)
    }, SHEET_CLOSE_MS)
  }

  const closeFilterPanel = () => {
    if (filterClosing) return
    filterCanDrag.current = false
    setFilterClosing(true)
    setFilterDragOffset(0)
    setTimeout(() => {
      setFilterPanelOpen(false)
      setFilterClosing(false)
    }, SHEET_CLOSE_MS)
  }

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [menuResponse, categoriesResponse] = await Promise.all([
        customerApi.getMenu(),
        customerApi.getCategories()
      ])

      if (menuResponse.success && menuResponse.data && Array.isArray(menuResponse.data)) {
        setMenuItems(menuResponse.data)
      } else {
        setError(menuResponse.error || t('failedToLoadMenu'))
      }

      if (categoriesResponse.success && categoriesResponse.data && Array.isArray(categoriesResponse.data)) {
        setCategories(categoriesResponse.data)
      }
    } catch (err) {
      setError(t('failedToLoadMenu'))
      console.error('Error loading data:', err)
    } finally {
      setLoading(false)
    }
  }

  const matchesSearch = (item: MenuItem) => {
    if (!searchTerm.trim()) return true
    const itemName = getLocalizedName(item.name, (item as any).name_ar, language)
    const itemDesc = getLocalizedDescription(item.description, (item as any).description_ar, language)
    const itemCode = (item as any).code
    const term = searchTerm.toLowerCase()
    return itemName.toLowerCase().includes(term) ||
      itemDesc.toLowerCase().includes(term) ||
      (itemCode && itemCode.toLowerCase().includes(term))
  }

  const popularItems = useMemo(() => {
    const list = menuItems.filter(item => item.is_popular && matchesSearch(item))
    return [...list].sort((a, b) => {
      const codeA = (a as any).code ?? ''
      const codeB = (b as any).code ?? ''
      if (codeA !== codeB) return codeA.localeCompare(codeB, undefined, { numeric: true })
      return getLocalizedName(a.name, (a as any).name_ar, language).toLowerCase().localeCompare(
        getLocalizedName(b.name, (b as any).name_ar, language).toLowerCase()
      )
    })
  }, [menuItems, searchTerm, language])

  // Featured carousel: 3 items (prefer popular, then fill from menu)
  // Exclusive deals: when user is searching, exclude items that match the search so they don't appear in exclusive
  const featuredItems = useMemo(() => {
    const pool = [...popularItems, ...menuItems.filter(m => !popularItems.some(p => p.id === m.id))]
    if (searchTerm.trim()) {
      return pool.filter(item => !matchesSearch(item)).slice(0, 3)
    }
    return pool.slice(0, 3)
  }, [menuItems, popularItems, searchTerm, language])

  // Categories that have items (for filter panel and display)
  const categoriesWithItemsBase = useMemo(() => {
    return categories.filter(cat => {
      const count = menuItems.filter(item => item.category_id === cat.id && matchesSearch(item)).length
      return count > 0
    })
  }, [categories, menuItems, searchTerm, language])

  // One section per category: filter by selected categories (if any), then optionally sorted
  const categoriesWithItems = useMemo(() => {
    let list = categoriesWithItemsBase
    if (selectedCategoryIds.length > 0) {
      const idSet = new Set(selectedCategoryIds)
      list = list.filter(cat => idSet.has(cat.id))
    }
    if (sortBy === 'category_az') {
      return [...list].sort((a, b) => {
        const nameA = getLocalizedName(a.name, (a as any).name_ar, language).toLowerCase()
        const nameB = getLocalizedName(b.name, (b as any).name_ar, language).toLowerCase()
        return nameA.localeCompare(nameB)
      })
    }
    return list
  }, [categoriesWithItemsBase, selectedCategoryIds, sortBy, language])

  /** Sort by code (null/empty last), then by name. */
  const sortByCode = (a: MenuItem, b: MenuItem) => {
    const codeA = (a as any).code ?? ''
    const codeB = (b as any).code ?? ''
    if (codeA !== codeB) return codeA.localeCompare(codeB, undefined, { numeric: true })
    const nameA = getLocalizedName(a.name, (a as any).name_ar, language).toLowerCase()
    const nameB = getLocalizedName(b.name, (b as any).name_ar, language).toLowerCase()
    return nameA.localeCompare(nameB)
  }

  const getItemsByCategory = (categoryId: string) => {
    let items = menuItems.filter(item => item.category_id === categoryId && matchesSearch(item))
    if (sortBy === 'price_asc') items = [...items].sort((a, b) => a.base_price - b.base_price)
    else if (sortBy === 'price_desc') items = [...items].sort((a, b) => b.base_price - a.base_price)
    else if (sortBy === 'name') {
      items = [...items].sort((a, b) => {
        const nameA = getLocalizedName(a.name, (a as any).name_ar, language).toLowerCase()
        const nameB = getLocalizedName(b.name, (b as any).name_ar, language).toLowerCase()
        return nameA.localeCompare(nameB)
      })
    } else {
      // default: sort by code (null/empty last), then name
      items = [...items].sort(sortByCode)
    }
    return items
  }

  if (loading) {
    return <HomeShimmer />
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafafa] p-4">
        <div className={`text-center max-w-md ${isRTL ? 'text-right' : 'text-left'}`}>
          <p className="text-red-600 text-lg mb-2 font-semibold">{t('error')}</p>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadData}
            className="px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
          >
            {t('tryAgain')}
          </button>
        </div>
      </div>
    )
  }

  const restaurantName = getLocalizedName(restaurantInfo?.restaurant_name || t('restaurantMenu'), restaurantInfo?.restaurant_name_ar, language)

  return (
    <div className="pb-4">
      {/* English: logo left, switcher right. Arabic: logo right, switcher left (swap DOM order + keep row LTR) */}
      <div className="max-w-7xl mx-auto px-4 pt-4 pb-2 flex items-start justify-between gap-4" dir="ltr">
        {isRTL ? (
          <>
            <div className="flex shrink-0 rounded-lg border border-gray-200 bg-white p-0.5" role="group" aria-label="Language">
              <button
                type="button"
                onClick={() => setLanguage('en')}
                className={`rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors ${language === 'en' ? 'bg-amber-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                aria-pressed={language === 'en'}
              >
                EN
              </button>
              <button
                type="button"
                onClick={() => setLanguage('ar')}
                className={`rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors ${language === 'ar' ? 'bg-amber-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                aria-pressed={language === 'ar'}
              >
                AR
              </button>
            </div>
            <div className="min-w-0 flex-1 flex flex-col items-end text-right">
              {restaurantInfo?.customer_frontend_logo_url ? (
                <>
                  <img
                    src={resolveImageUrl(restaurantInfo.customer_frontend_logo_url)}
                    alt={restaurantName}
                    className="max-h-16 w-auto max-w-[200px] object-contain object-right"
                  />
                  <p className="text-sm text-gray-500 mt-1.5">{t('restaurantTagline')}</p>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-gray-900">{restaurantName}</h2>
                  <p className="text-sm text-gray-500 mt-0.5">{t('restaurantTagline')}</p>
                </>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="min-w-0 flex-1">
              {restaurantInfo?.customer_frontend_logo_url ? (
                <div className="flex flex-col items-start text-left">
                  <img
                    src={resolveImageUrl(restaurantInfo.customer_frontend_logo_url)}
                    alt={restaurantName}
                    className="max-h-16 w-auto max-w-[200px] object-contain object-left"
                  />
                  <p className="text-sm text-gray-500 mt-1.5">{t('restaurantTagline')}</p>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-gray-900">{restaurantName}</h2>
                  <p className="text-sm text-gray-500 mt-0.5">{t('restaurantTagline')}</p>
                </>
              )}
            </div>
            <div className="flex shrink-0 rounded-lg border border-gray-200 bg-white p-0.5" role="group" aria-label="Language">
              <button
                type="button"
                onClick={() => setLanguage('en')}
                className={`rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors ${language === 'en' ? 'bg-amber-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                aria-pressed={language === 'en'}
              >
                EN
              </button>
              <button
                type="button"
                onClick={() => setLanguage('ar')}
                className={`rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors ${language === 'ar' ? 'bg-amber-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                aria-pressed={language === 'ar'}
              >
                AR
              </button>
            </div>
          </>
        )}
      </div>

      {/* Search - sticky so it stays at top when scrolling (top-8 = below layout's white strip) */}
      <div className="sticky top-8 z-10 bg-[#fafafa] pb-3 pt-1 -mt-1">
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="relative">
            <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none`} />
            <input
              type="text"
              placeholder={t('searchFoodPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400 outline-none`}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
        </div>
      </div>

      {/* Featured: Exclusive Deals - 3 items carousel, each with a different color */}
      {featuredItems.length > 0 && (() => {
        const dealColors = [
          { card: 'from-amber-400 to-orange-500', imageBg: 'bg-amber-300/30', label: 'text-amber-100', button: 'text-amber-600 hover:bg-amber-50' },
          { card: 'from-rose-400 to-pink-500', imageBg: 'bg-rose-300/30', label: 'text-rose-100', button: 'text-rose-600 hover:bg-rose-50' },
          { card: 'from-emerald-400 to-teal-500', imageBg: 'bg-emerald-300/30', label: 'text-emerald-100', button: 'text-emerald-600 hover:bg-emerald-50' },
        ]
        return (
          <div className="max-w-7xl mx-auto px-4 pb-4">
            <div className="flex overflow-x-auto gap-4 snap-x snap-mandatory scrollbar-hide -mx-4 px-4">
              {featuredItems.map((item, index) => {
                const colors = dealColors[index % dealColors.length]
                return (
                  <div
                    key={item.id}
                    className={`flex-shrink-0 w-[85%] max-w-md snap-center rounded-2xl overflow-hidden bg-gradient-to-br ${colors.card} shadow-lg`}
                  >
                    <div className="flex min-h-[140px]">
                      <div className={`relative w-2/5 min-h-[140px] overflow-hidden ${colors.imageBg}`}>
                        {item.image ? (
                          <img
                            src={item.image}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover object-center"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-white/80 text-4xl">🍽</div>
                        )}
                      </div>
                      <div className="flex-1 p-3 flex flex-col justify-between text-white">
                        <div>
                          <p className={`${colors.label} text-[10px] font-medium uppercase tracking-wide`}>
                            {t('exclusiveDeals')}
                          </p>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            {(item as any).code && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium font-mono bg-white/25 text-white">
                                {(item as any).code}
                              </span>
                            )}
                            <h3 className="font-bold text-base line-clamp-2">
                              {getLocalizedName(item.name, (item as any).name_ar, language)}
                            </h3>
                          </div>
                          <p className="text-base font-bold mt-0.5">{formatPrice(item.base_price)}</p>
                          {(item.calories != null || item.steps_to_burn != null) && (
                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                              {item.calories != null && (
                                <span className="inline-flex items-center rounded-md bg-white/20 px-1.5 py-0.5 text-[10px] font-medium text-white">
                                  <Flame className="mr-0.5 h-3 w-3" />
                                  {item.calories} cal
                                </span>
                              )}
                              {item.steps_to_burn != null && (
                                <span className="inline-flex items-center rounded-md bg-white/20 px-1.5 py-0.5 text-[10px] font-medium text-white">
                                  <Footprints className="mr-0.5 h-3 w-3" />
                                  {item.steps_to_burn} steps
                                </span>
                              )}
                            </div>
                          )}
                          {item.allergens && item.allergens.length > 0 && (
                            <div className="mt-1.5">
                              <AllergenIcons allergenIds={item.allergens} allergenMap={allergenMap} size={18} className="[&_span]:bg-white/20" />
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => addItem({
                            menu_item_id: item.id,
                            name: getLocalizedName(item.name, (item as any).name_ar, language),
                            base_price: item.base_price,
                            quantity: 1,
                            image: item.image,
                          })}
                          className={`mt-2 bg-white font-semibold text-xs py-1.5 px-3 rounded-full w-fit transition-colors ${colors.button}`}
                        >
                          {t('addToCart')}
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })()}

      {/* Most Popular - horizontal scroll */}
      {popularItems.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className={`flex items-center justify-between mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <h3 className="text-lg font-bold text-gray-900">{t('mostPopular')}</h3>
            <Link to="/" className="text-amber-500 font-medium text-sm">
              {t('seeAll')}
            </Link>
          </div>
          <div className="flex gap-4 overflow-x-auto scrollbar-hide -mx-4 px-4">
            {popularItems.map(item => (
              <Link
                key={item.id}
                to={`/item/${item.id}`}
                className="flex-shrink-0 w-36 bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
              >
                <div className="aspect-square bg-gray-100">
                  {item.image ? (
                    <img src={item.image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-3xl">🍽</div>
                  )}
                </div>
                <div className="p-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {(item as any).code && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium font-mono bg-amber-200 text-amber-900">
                        {(item as any).code}
                      </span>
                    )}
                    <h4 className="font-semibold text-gray-900 text-sm line-clamp-2">
                      {getLocalizedName(item.name, (item as any).name_ar, language)}
                    </h4>
                  </div>
                  <p className="text-amber-500 font-bold text-sm mt-0.5">{formatPrice(item.base_price)}</p>
                  {(item.calories != null || item.steps_to_burn != null) && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {item.calories != null && (
                        <span className="inline-flex items-center rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700">
                          <Flame className="mr-0.5 h-3 w-3" />
                          {item.calories} cal
                        </span>
                      )}
                      {item.steps_to_burn != null && (
                        <span className="inline-flex items-center rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                          <Footprints className="mr-0.5 h-3 w-3" />
                          {item.steps_to_burn} steps
                        </span>
                      )}
                    </div>
                  )}
                  {item.allergens && item.allergens.length > 0 && (
                    <div className="mt-1">
                      <AllergenIcons allergenIds={item.allergens} allergenMap={allergenMap} size={16} />
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* One section per category: heading + horizontal scroll + See All */}
      {categoriesWithItems.map(cat => {
        const items = getItemsByCategory(cat.id)
        if (items.length === 0) return null
        return (
          <div key={cat.id} className="max-w-7xl mx-auto px-4 py-4">
            <div className={`flex items-center justify-between mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <h3 className="text-lg font-bold text-gray-900">
                {getLocalizedName(cat.name, (cat as any).name_ar, language)}
              </h3>
              <Link
                to={`/category/${cat.id}`}
                className="text-amber-500 font-medium text-sm"
              >
                {t('seeAll')}
              </Link>
            </div>
            <div className="flex gap-4 overflow-x-auto scrollbar-hide -mx-4 px-4">
              {items.map(item => (
                <Link
                  key={item.id}
                  to={`/item/${item.id}`}
                  className="flex-shrink-0 w-36 bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
                >
                  <div className="aspect-square bg-gray-100">
                    {item.image ? (
                      <img src={item.image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl">🍽</div>
                    )}
                  </div>
                  <div className="p-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {(item as any).code && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium font-mono bg-amber-200 text-amber-900">
                          {(item as any).code}
                        </span>
                      )}
                      <h4 className="font-semibold text-gray-900 text-sm line-clamp-2">
                        {getLocalizedName(item.name, (item as any).name_ar, language)}
                      </h4>
                    </div>
                    <p className="text-amber-500 font-bold text-sm mt-0.5">{formatPrice(item.base_price)}</p>
                    {(item.calories != null || item.steps_to_burn != null) && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {item.calories != null && (
                          <span className="inline-flex items-center rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700">
                            <Flame className="mr-0.5 h-3 w-3" />
                            {item.calories} cal
                          </span>
                        )}
                        {item.steps_to_burn != null && (
                          <span className="inline-flex items-center rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                            <Footprints className="mr-0.5 h-3 w-3" />
                            {item.steps_to_burn} steps
                          </span>
                        )}
                      </div>
                    )}
                    {item.allergens && item.allergens.length > 0 && (
                      <div className="mt-1">
                        <AllergenIcons allergenIds={item.allergens} allergenMap={allergenMap} size={16} />
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )
      })}

      {searchTerm && menuItems.filter(matchesSearch).length === 0 && (
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <p className="text-gray-500">{t('noItemsFound')}</p>
        </div>
      )}

      {/* Floating Sort | Filter pill bar - above bottom nav, darker gray, white text, compact */}
      <div className="fixed bottom-24 left-1/2 z-40 w-auto max-w-[200px] -translate-x-1/2 px-3" dir="ltr">
        <div className="flex overflow-hidden rounded-full bg-gray-500 shadow-md">
          <button
            type="button"
            onClick={() => { setSortPanelOpen(true); setFilterPanelOpen(false) }}
            className="flex flex-1 items-center justify-center gap-1 py-2 px-2.5 text-white transition-colors hover:bg-gray-600"
            aria-label={t('sort')}
          >
            <ArrowUpDown className="w-3.5 h-3.5" />
            <span className="text-[10px] font-medium">{t('sort')}</span>
          </button>
          <div className="w-px shrink-0 bg-white/40" aria-hidden />
          <button
            type="button"
            onClick={() => { setFilterPanelOpen(true); setSortPanelOpen(false) }}
            className="flex flex-1 items-center justify-center gap-1 py-2 px-2.5 text-white transition-colors hover:bg-gray-600"
            aria-label={t('filter')}
          >
            <Filter className="w-3.5 h-3.5" />
            <span className="text-[10px] font-medium">{t('filter')}</span>
          </button>
        </div>
      </div>

      {/* Sort bottom sheet */}
      {(sortPanelOpen || sortClosing) && (
        <>
          <div
            className={`fixed inset-0 z-50 bg-black/40 ${sortClosing ? 'animate-sheet-overlay-out' : 'animate-sheet-overlay'}`}
            style={!sortClosing && sortDragOffset > 0 ? { opacity: 1 - sortDragOffset / 400 * 0.5 } : undefined}
            onClick={closeSortPanel}
            aria-hidden
          />
          <div
            className={`fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl bg-white pb-safe shadow-xl ${sortClosing ? 'animate-sheet-slide-down' : 'animate-sheet-slide-up'} ${sortSnapBack ? 'transition-transform duration-200 ease-out' : ''}`}
            style={sortDragOffset > 0 ? { transform: `translateY(${sortDragOffset}px)` } : undefined}
            role="dialog"
            aria-label={t('sortBy')}
          >
            {/* Drag handle */}
            <div
              className="flex cursor-grab active:cursor-grabbing justify-center pt-3 pb-2 touch-none"
              onTouchStart={(e) => handleSortDragStart(e.touches[0].clientY)}
              onTouchMove={(e) => { e.preventDefault(); handleSortDragMove(e.touches[0].clientY) }}
              onTouchEnd={handleSortDragEnd}
              onMouseDown={(e) => { handleSortDragStart(e.clientY); bindSortMouseDrag() }}
            >
              <div className="h-1 w-12 rounded-full bg-gray-300" />
            </div>
            <div className="p-4 pt-0 pb-24">
            <p className="mb-3 text-sm font-semibold text-gray-700">{t('sortBy')}</p>
            <div className="space-y-1">
              {[
                { value: 'default' as const, label: t('sortDefault') },
                { value: 'price_asc' as const, label: t('sortPriceLowToHigh') },
                { value: 'price_desc' as const, label: t('sortPriceHighToLow') },
                { value: 'name' as const, label: t('sortNameAtoZ') },
                { value: 'category_az' as const, label: t('sortCategoryAtoZ') },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    setSortBy(opt.value)
                    closeSortPanel()
                  }}
                  className={`block w-full rounded-lg px-4 py-3 text-left text-sm transition-colors ${
                    sortBy === opt.value ? 'bg-amber-50 text-amber-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            </div>
          </div>
        </>
      )}

      {/* Filter bottom sheet - categories */}
      {(filterPanelOpen || filterClosing) && (
        <>
          <div
            className={`fixed inset-0 z-50 bg-black/40 ${filterClosing ? 'animate-sheet-overlay-out' : 'animate-sheet-overlay'}`}
            style={!filterClosing && filterDragOffset > 0 ? { opacity: 1 - filterDragOffset / 400 * 0.5 } : undefined}
            onClick={closeFilterPanel}
            aria-hidden
          />
          <div
            className={`fixed bottom-0 left-0 right-0 z-50 max-h-[70vh] flex flex-col rounded-t-2xl bg-white shadow-xl ${filterClosing ? 'animate-sheet-slide-down' : 'animate-sheet-slide-up'} ${filterSnapBack ? 'transition-transform duration-200 ease-out' : ''}`}
            style={filterDragOffset > 0 ? { transform: `translateY(${filterDragOffset}px)` } : undefined}
            role="dialog"
            aria-label={t('filterByCategory')}
          >
            {/* Drag handle */}
            <div
              className="flex cursor-grab active:cursor-grabbing justify-center pt-3 pb-2 touch-none"
              onTouchStart={(e) => handleFilterDragStart(e.touches[0].clientY)}
              onTouchMove={(e) => { e.preventDefault(); handleFilterDragMove(e.touches[0].clientY) }}
              onTouchEnd={handleFilterDragEnd}
              onMouseDown={(e) => { handleFilterDragStart(e.clientY); bindFilterMouseDrag() }}
            >
              <div className="h-1 w-12 rounded-full bg-gray-300" />
            </div>
            <div className="flex items-center justify-between border-b border-gray-200 p-4">
              <p className="text-sm font-semibold text-gray-700">{t('filterByCategory')}</p>
              <button
                type="button"
                onClick={closeFilterPanel}
                className="text-amber-600 font-medium text-sm"
              >
                {t('close')}
              </button>
            </div>
            <div className="flex-1 overflow-y-auto scrollbar-hide p-4">
              <label className="mb-3 flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 p-3">
                <input
                  type="checkbox"
                  checked={selectedCategoryIds.length === 0}
                  onChange={() => setSelectedCategoryIds([])}
                  className="h-4 w-4 rounded border-gray-300 text-amber-500 focus:ring-amber-400"
                />
                <span className="text-sm text-gray-700">{t('selectAllCategories')}</span>
              </label>
              {categoriesWithItemsBase.map(cat => {
                const checked = selectedCategoryIds.length === 0 || selectedCategoryIds.includes(cat.id)
                return (
                  <label
                    key={cat.id}
                    className="mb-2 flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 p-3"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        if (selectedCategoryIds.length === 0) {
                          setSelectedCategoryIds(categoriesWithItemsBase.map(c => c.id).filter(id => id !== cat.id))
                        } else if (selectedCategoryIds.includes(cat.id)) {
                          setSelectedCategoryIds(selectedCategoryIds.filter(id => id !== cat.id))
                        } else {
                          setSelectedCategoryIds([...selectedCategoryIds, cat.id])
                        }
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-amber-500 focus:ring-amber-400"
                    />
                    <span className="text-sm text-gray-700">
                      {getLocalizedName(cat.name, (cat as any).name_ar, language)}
                    </span>
                  </label>
                )
              })}
            </div>
            <div className="border-t border-gray-200 p-4">
              <button
                type="button"
                onClick={closeFilterPanel}
                className="w-full rounded-xl bg-amber-500 py-3 font-medium text-white hover:bg-amber-600"
              >
                {t('applyFilters')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
